/**
 * Pipeline Store (Zustand with IndexedDB persistence)
 * 
 * Stores saved pipelines.
 * Uses IndexedDB for structured storage.
 * 
 * @see docs/Research/DesignSpecsClaude.md Section 6.3.2
 */

import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

import type { IPipelineStep } from '@/interfaces/IPipeline';
import type { ISavedPipeline } from '@/interfaces/IStorage';
import { generateId } from '@/lib/utils';
import { getMigrationVisualizerIds } from '@/lib/visualizerManifest';

interface PipelineStoreState {
  /** Saved pipelines */
  savedPipelines: ISavedPipeline[];
  
  // Pipeline actions
  savePipeline: (
    name: string,
    steps: IPipelineStep[],
    description?: string,
    tags?: string[],
    meta?: { 
      inputSemanticType?: string; 
      outputSemanticType?: string; 
      variables?: Record<string, string>;
      inputData?: string;
      inputType?: string;
    }
  ) => string;
  updatePipeline: (id: string, updates: Partial<Omit<ISavedPipeline, 'id' | 'createdAt'>>) => void;
  deletePipeline: (id: string) => void;
  getPipeline: (id: string) => ISavedPipeline | undefined;
  reset: () => void;
}

/**
 * Pipeline store with localStorage persistence.
 * TODO: Migrate to IndexedDB for larger storage capacity.
 */

/**
 * Reorder pipeline steps so visualizers only appear at head/tail.
 * Any mid-pipeline visualizers are moved to the tail.
 * Works on step-like objects (with at least a `toolId`).
 */
/**
 * Visualizer tool IDs derived from the manifest.
 * Safe at module-load time — visualizerManifest.ts is a leaf module.
 */
const VISUALIZER_TOOL_IDS = getMigrationVisualizerIds();

function reorderVisualizerSteps<T extends { toolId: string }>(steps: T[]): T[] {
  const isViz = (s: T) => VISUALIZER_TOOL_IDS.has(s.toolId);

  // Find leading visualizers
  let headEnd = 0;
  while (headEnd < steps.length && isViz(steps[headEnd])) headEnd++;

  // Find trailing visualizers
  let tailStart = steps.length;
  while (tailStart > headEnd && isViz(steps[tailStart - 1])) tailStart--;

  const headVizs = steps.slice(0, headEnd);
  const tailVizs = steps.slice(tailStart);
  const middle = steps.slice(headEnd, tailStart);

  // Separate mid-pipeline visualizers from transforms
  const transforms: T[] = [];
  const midVizs: T[] = [];
  for (const s of middle) {
    if (isViz(s)) {
      midVizs.push(s);
    } else {
      transforms.push(s);
    }
  }

  return [...headVizs, ...transforms, ...midVizs, ...tailVizs];
}

export const usePipelineStore = create<PipelineStoreState>()(
  persist(
    (set, get) => ({
      savedPipelines: [],
      
      // Pipeline actions
      savePipeline: (name, steps, description, tags, meta) => {
        const id = generateId();
        const now = new Date();
        
        const newPipeline: ISavedPipeline = {
          id,
          name,
          description,
          inputSemanticType: meta?.inputSemanticType,
          outputSemanticType: meta?.outputSemanticType,
          steps: steps.map(({ toolId, label, config, isEnabled, panelRole }) => ({ toolId, label, config, isEnabled, ...(panelRole && { panelRole }) })),
          variables: meta?.variables,
          inputData: meta?.inputData,
          inputType: meta?.inputType,
          createdAt: now,
          updatedAt: now,
          tags,
        };
        
        set((state) => ({
          savedPipelines: [...state.savedPipelines, newPipeline],
        }));
        
        return id;
      },
      
      updatePipeline: (id, updates) => {
        set((state) => ({
          savedPipelines: state.savedPipelines.map((pipeline) =>
            pipeline.id === id
              ? { ...pipeline, ...updates, updatedAt: new Date() }
              : pipeline
          ),
        }));
      },
      
      deletePipeline: (id) => {
        set((state) => ({
          savedPipelines: state.savedPipelines.filter((p) => p.id !== id),
        }));
      },
      
      getPipeline: (id) => {
        return get().savedPipelines.find((p) => p.id === id);
      },

      reset: () => {
        set({ savedPipelines: [] });
      },
    }),
    {
      name: 'swedevtools-pipelines',
      version: 4,
      migrate: (persisted, version) => {
        const state = { savedPipelines: [], ...(persisted as Record<string, unknown>) } as unknown as PipelineStoreState;
        
        // Migrate from v1 to v2: old pipelines won't have inputData or inputType
        if (version != null && version < 2) {
          state.savedPipelines = state.savedPipelines.map((pipeline) => ({
            ...pipeline,
            inputData: pipeline.inputData,
            inputType: pipeline.inputType,
          }));
        }

        // Migrate from v2 to v3: reorder visualizer steps to head/tail only
        if (version != null && version < 3) {
          state.savedPipelines = state.savedPipelines.map((pipeline) => ({
            ...pipeline,
            steps: reorderVisualizerSteps(pipeline.steps),
          }));
        }

        // Migrate from v3 to v4: strip inputData from persisted pipelines (data safety)
        if (version != null && version < 4) {
          state.savedPipelines = state.savedPipelines.map((pipeline) => {
            const { inputData: _stripped, ...rest } = pipeline;
            return rest as ISavedPipeline;
          });
        }
        
        return state;
      },
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        savedPipelines: state.savedPipelines.map(({ inputData: _stripped, ...rest }) => rest),
      }),
    }
  )
);

